import _ from "lodash";

export default class NorthernIrishPositionalSystem {
    constructor(systemID, route, position, subposition, sources) {
        this.system = systemID;
        this.route = route;
        this.position = position;
        this.subposition = subposition;
        this.sources = sources;
        const absPosition = Math.abs(position);
        const sign = Math.sign(position);
        const absMile = Math.floor(absPosition / 20) / 4; // represent position as fractions of miles
        this.mileRemainder = absPosition / 80 - absMile;
        this.mile = sign * absMile;

        this.chain = Math.floor(this.mileRemainder * 80);
        this.meter = Math.floor(this.mileRemainder * NorthernIrishPositionalSystem.meters_per_mile);
    }

    static meters_per_mile = 1609.34;

    static from_fields(system_id, display_format, fields) {
        if (display_format === "elr_mile_meter") {
            const position = fields.MILE * 80 + fields.METER / NorthernIrishPositionalSystem.meters_per_chain;
            return new NorthernIrishPositionalSystem(system_id, fields.ROUTE, position, fields.TRACK, []);
        } else {
            const position = fields.MILE * 80 + fields.CHAIN;
            return new NorthernIrishPositionalSystem(system_id, fields.ROUTE, position, fields.TRACK, []);
        }
    }

    static display_formats = {
        elr_mile_meter: "Route ID Mile and Meter",
        elr_mile_chain: "Route ID Mile and Chain",
    };

    static display_fields = {
        ROUTE: {
            name: "Route ID",
            rules: [{ required: true, min: 1, max: 5 }],
        },
        MILE: {
            name: "Mile",
            rules: [{ required: true, type: "integer" }],
        },
        METER: {
            name: "Meter",
            rules: [{ required: true, type: "integer", min: 0, max: 401 }],
        },
        CHAIN: {
            name: "Chain",
            rules: [{ required: true, type: "integer", min: 0, max: 19 }],
        },
    };

    fields(display_format) {
        return NorthernIrishPositionalSystem.fields(display_format);
    }

    static fields(display_format) {
        if (display_format === "elr_mile_meter") {
            return ["ROUTE", "MILE", "METER"];
        } else {
            return ["ROUTE", "MILE", "CHAIN"];
        }
    }

    static routeAbbreviationDict = {
        KJA: "Knockmore Junction to Antrim",
        BGD: "Bleach Green to Londonderry",
        CTP: "Colraine to Portrush",
        BTB: "Border to Bangor",
        LJL: "Lagan Junction to Larne",
        CEG: "Central Junction to GCS",
        CIW: "City Junction to Westlink Junction",
    };

    to_mile_and_chain_string() {
        return `${this.mile}m ${this.chain}c`;
    }

    to_mile_and_meter_string() {
        return `${this.mile}mi ${this.meter}m`;
    }

    to_string(display_format, datum_offsets) {
        let position;
        if (display_format === "elr_mile_meter") {
            position = this.to_mile_and_meter_string();
            return `${this.lookup_route_name(this.route)} ${position}`;
        } else {
            position = this.to_mile_and_chain_string();
            return `${this.lookup_route_name(this.route)} ${position}`;
        }
    }

    lookup_route_name(routeID) {
        return _.get(NorthernIrishPositionalSystem.routeAbbreviationDict, [routeID], routeID);
    }

    field_value(field, datum_offsets) {
        switch (field) {
            case "ROUTE":
                return this.lookup_route_name(this.route);
            case "MILE":
                return this.mile;
            case "TRACK":
                return this.subposition;
            case "CHAIN":
                return this.chain;
            case "METER":
                return this.meter;
            default:
                return null;
        }
    }

    displayFields(display_format) {
        return NorthernIrishPositionalSystem.displayFields(display_format);
    }

    static displayFields(display_format) {
        if (display_format === "elr_mile_meter") {
            return ["ROUTE", "MILE", "METER"];
        } else {
            return ["ROUTE", "MILE", "CHAIN"];
        }
    }

    static from_search(system_id, fields) {
        const position = parseInt(fields.paramTwo) * 80 + fields.paramThree;
        return new NorthernIrishPositionalSystem(system_id, fields.paramOne, position, null);
    }
}
